import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuctionGrid } from "@/components/auction-grid";
import { fetchAllActive, isBuyNowAvailable, sortFeaturedFirst } from "@/lib/auctions";
import { fetchTrustSellerSets } from "@/lib/trust-seller-sets";
import { CardGridSkeleton, ErrorState } from "@/components/data-state";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { parseSmartSearch } from "@/lib/smart-search.functions";
import { matchesSmartFilters, type SmartSearchFilters } from "@/lib/smart-search";
import { findCategoryByQ, BROWSE_CATEGORIES } from "@/lib/browse-categories";
import type { AuctionCardData } from "@/components/auction-card";
import { ALL_GRADERS, GRADER_SHORT, SELLER_GRADING_COMPANIES, sortedGradeFilters, formatGradeLabel, CARD_TYPES, CARD_TYPE_LABEL, PRODUCT_TYPES, PRODUCT_TYPE_LABEL, ACCESSORY_CATEGORIES, ACCESSORY_CATEGORY_LABEL, COMPATIBLE_GAMES, labelForCondition, CARD_LANGUAGES, FINISH_OPTIONS, EDITION_OPTIONS, TCG_GAMES, RARITY_OPTIONS, TCG_GAME_LABEL, RARITY_LABEL } from "@/lib/grading/companies";
import { socialImageMeta } from "@/lib/seo";
import { MAIN_CATEGORIES, CARD_SUBCATEGORIES, subcategoriesFor, rowCategory, matchesCardSubcategory, type AttributeField } from "@/lib/marketplace/categories";
import { useTranslation } from "@/i18n";

const DEFAULT_TITLE = "Live Trading Card Auctions — BidNova Auctions UK";
const DEFAULT_DESCRIPTION = "Browse live UK auctions for graded Pokémon, One Piece, Yu-Gi-Oh!, Dragon Ball and sports trading cards. Bid in GBP with Stripe-secured payments and buyer protection.";

const AREA_META: Record<string, { title: string; description: string }> = {
  cards: { title: "Trading Card Auctions UK — Raw, Graded & Sealed | BidNova Auctions", description: "Bid on raw, graded and sealed trading cards — Pokémon, One Piece, Yu-Gi-Oh!, sports and more — on BidNova Auctions UK." },
  collectibles: { title: "Collectibles Auctions UK — Funko Pop!, Figures & Statues | BidNova Auctions", description: "Bid on Funko Pop!, anime and action figures, statues, designer toys and collectible merchandise on BidNova Auctions UK." },
  fashion: { title: "Fashion Auctions UK — Streetwear, Sneakers & Accessories | BidNova Auctions", description: "Bid on T-shirts, hoodies, jackets, sneakers, hats, bags and fashion accessories on BidNova Auctions UK." },
};

const auctionsSearchSchema = z.object({
  q: fallback(z.string().optional(), undefined),
  product_type: fallback(z.string().optional(), undefined),
  accessory_category: fallback(z.string().optional(), undefined),
  card_type: fallback(z.string().optional(), undefined),
  grader: fallback(z.string().optional(), undefined),
  grade: fallback(z.string().optional(), undefined),
  brand: fallback(z.string().optional(), undefined),
  condition: fallback(z.string().optional(), undefined),
  compatible_game: fallback(z.string().optional(), undefined),
  product_line_set: fallback(z.string().optional(), undefined),
  card_language: fallback(z.string().optional(), undefined),
  finish: fallback(z.string().optional(), undefined),
  edition: fallback(z.string().optional(), undefined),
  card_set: fallback(z.string().optional(), undefined),
  tcg_game: fallback(z.string().optional(), undefined),
  rarity: fallback(z.string().optional(), undefined),
  autographed: fallback(z.string().optional(), undefined),
  serial_numbered: fallback(z.string().optional(), undefined),
  trust: fallback(z.string().optional(), undefined),
  category: fallback(z.string().optional(), undefined),
  subcategory: fallback(z.string().optional(), undefined),
  attrs: fallback(z.string().optional(), undefined),
  price_min: fallback(z.string().optional(), undefined),
  price_max: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/auctions/")({
  validateSearch: zodValidator(auctionsSearchSchema),
  loaderDeps: ({ search }) => ({ q: search.q, area: search.category }),
  loader: ({ deps }) => {
    const cat = findCategoryByQ(deps.q);
    return { category: cat, q: deps.q ?? "", area: deps.area ?? "" };
  },
  head: ({ loaderData }) => {
    const cat = loaderData?.category ?? null;
    const q = loaderData?.q ?? "";
    const area = AREA_META[loaderData?.area ?? ""];
    const title = cat ? cat.title : area ? area.title : DEFAULT_TITLE;
    const description = cat ? cat.description : area ? area.description : DEFAULT_DESCRIPTION;
    const canonical = cat
      ? `https://bidnovaauctions.uk/auctions?q=${encodeURIComponent(cat.q)}`
      : area
      ? `https://bidnovaauctions.uk/auctions?category=${loaderData?.area}`
      : "https://bidnovaauctions.uk/auctions";
    // Search-only pages that don't map to a known category should not be indexed
    // to avoid thin/duplicate results for arbitrary user queries.
    const robots = cat || !q ? "index,follow" : "noindex,follow";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: robots },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "website" },
        ...socialImageMeta(),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
              { "@type": "ListItem", position: 2, name: "Auctions", item: "https://bidnovaauctions.uk/auctions" },
              ...(cat
                ? [
                    {
                      "@type": "ListItem",
                      position: 3,
                      name: cat.label,
                      item: canonical,
                    },
                  ]
                : []),
            ],
          }),
        },
      ],
    };
  },
  component: BrowsePage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

// Re-export for consumers that need the shared list without a second import path.
export { BROWSE_CATEGORIES };


type PurchaseFilter = "all" | "auction_only" | "buy_now" | "auction_plus_bin";
type SortKey =
  | "ending_soon"
  | "newly_listed"
  | "current_bid_high"
  | "bin_price_low"
  | "bin_price_high";

function filterAndSort(
  data: AuctionCardData[],
  purchase: PurchaseFilter,
  sort: SortKey,
): AuctionCardData[] {
  const filtered = data.filter((a) => {
    const bin = isBuyNowAvailable(a);
    const hasBids = (a.bid_count ?? 0) > 0;
    if (purchase === "auction_only") return !bin;
    if (purchase === "buy_now") return bin;
    if (purchase === "auction_plus_bin") return bin && hasBids;
    return true;
  });
  const sorted = [...filtered];
  switch (sort) {
    case "newly_listed":
      // fetchAllActive orders by end_time ascending; best-effort proxy is id order — leave stable.
      sorted.reverse();
      break;
    case "current_bid_high":
      sorted.sort(
        (a, b) => (b.current_bid_pence ?? b.starting_price_pence) - (a.current_bid_pence ?? a.starting_price_pence),
      );
      break;
    case "bin_price_low":
      sorted.sort((a, b) => (a.buy_now_price_pence ?? Number.POSITIVE_INFINITY) - (b.buy_now_price_pence ?? Number.POSITIVE_INFINITY));
      break;
    case "bin_price_high":
      sorted.sort((a, b) => (b.buy_now_price_pence ?? -1) - (a.buy_now_price_pence ?? -1));
      break;
    case "ending_soon":
    default:
      sorted.sort((a, b) => new Date(a.end_time).getTime() - new Date(b.end_time).getTime());
      break;
  }
  return sortFeaturedFirst(sorted);
}

function BrowsePage() {
  const { t } = useTranslation("marketing");
  const { category, q: initialQ } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const productTypeFilter = search.product_type ?? "";
  const accessoryCategoryFilter = search.accessory_category ?? "";
  const cardTypeFilter = search.card_type ?? "";
  const graderFilter = (search.grader ?? "").toUpperCase();
  const gradeFilter = search.grade ?? "";
  const brandFilter = search.brand ?? "";
  const conditionFilter = search.condition ?? "";
  const compatibleGameFilter = search.compatible_game ?? "";
  const productLineFilter = search.product_line_set ?? "";
  const cardLanguageFilter = search.card_language ?? "";
  const finishFilter = search.finish ?? "";
  const editionFilter = search.edition ?? "";
  const cardSetFilter = search.card_set ?? "";
  const tcgGameFilter = search.tcg_game ?? "";
  const rarityFilter = search.rarity ?? "";
  const autographedFilter = search.autographed ?? "";
  const serialNumberedFilter = search.serial_numbered ?? "";
  const categoryFilter = search.category ?? "";
  const subcategoryFilter = search.subcategory ?? "";
  const attrFilters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const part of (search.attrs ?? "").split("|")) {
      const i = part.indexOf(":");
      if (i > 0) out[part.slice(0, i)] = part.slice(i + 1);
    }
    return out;
  }, [search.attrs]);
  const priceMin = search.price_min ? Math.round(Number(search.price_min) * 100) : null;
  const priceMax = search.price_max ? Math.round(Number(search.price_max) * 100) : null;
  const showCardFilters = !categoryFilter || categoryFilter === "cards";
  const trustFilters = new Set((search.trust ?? "").split(",").filter(Boolean));
  const trustVerified = trustFilters.has("verified");
  const trustPartner = trustFilters.has("partner");
  const trustFeatured = trustFilters.has("featured");
  const toggleTrust = (key: "verified" | "partner" | "featured") => {
    const next = new Set(trustFilters);
    next.has(key) ? next.delete(key) : next.add(key);
    updateSearch({ trust: next.size ? Array.from(next).join(",") : undefined });
  };
  const updateSearch = (patch: Record<string, string | undefined>) => {
    navigate({
      search: (prev: Record<string, string | undefined>) => {
        const next: Record<string, string | undefined> = { ...prev, ...patch };
        for (const [k, v] of Object.entries(patch)) {
          if (!v) delete next[k];
        }
        return next as never;
      },
    });
  };
  const setAttrFilter = (key: string, value: string | undefined) => {
    const next = { ...attrFilters };
    if (value) next[key] = value; else delete next[key];
    const enc = Object.entries(next).map(([k, v]) => `${k}:${v}`).join("|");
    updateSearch({ attrs: enc || undefined });
  };
  const clearAllFilters = () => updateSearch({
    category: undefined,
    subcategory: undefined,
    attrs: undefined,
    price_min: undefined,
    price_max: undefined,
    product_type: undefined,
    accessory_category: undefined,
    card_type: undefined,
    grader: undefined,
    grade: undefined,
    brand: undefined,
    condition: undefined,
    compatible_game: undefined,
    product_line_set: undefined,
    card_language: undefined,
    finish: undefined,
    edition: undefined,
    card_set: undefined,
    tcg_game: undefined,
    rarity: undefined,
    autographed: undefined,
    serial_numbered: undefined,
    trust: undefined,
  });

  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["all-active"],
    queryFn: fetchAllActive,
    refetchInterval: 30000,
  });
  const { data: trustSets } = useQuery({
    queryKey: ["trust-seller-sets"],
    queryFn: fetchTrustSellerSets,
    staleTime: 60_000,
    enabled: trustVerified || trustPartner || trustFeatured,
  });
  const [purchase, setPurchase] = useState<PurchaseFilter>("all");
  const [sort, setSort] = useState<SortKey>("ending_soon");
  const [smartQuery, setSmartQuery] = useState(initialQ ?? "");
  const [smartFilters, setSmartFilters] = useState<SmartSearchFilters | null>(null);
  const [smartRunning, setSmartRunning] = useState(false);
  const parseFn = useServerFn(parseSmartSearch);

  const filtered = useMemo(() => filterAndSort(data ?? [], purchase, sort), [data, purchase, sort]);
  const qLower = (initialQ ?? "").trim().toLowerCase();
  const qFiltered = useMemo(
    () => (qLower ? filtered.filter((a) => {
      const t = (a.title ?? "").toLowerCase();
      const brand = ((a as { brand?: string | null }).brand ?? "").toLowerCase();
      const line = ((a as { product_line_set?: string | null }).product_line_set ?? "").toLowerCase();
      const game = ((a as { compatible_game?: string | null }).compatible_game ?? "").toLowerCase();
      const acc = ((a as { accessory_category?: string | null }).accessory_category ?? "").toLowerCase();
      return t.includes(qLower) || brand.includes(qLower) || line.includes(qLower) || game.includes(qLower) || acc.includes(qLower);
    }) : filtered),
    [filtered, qLower],
  );
  const urlFiltered = useMemo(() => {
    return qFiltered.filter((a) => {
      const area = rowCategory(a);
      if (categoryFilter && area !== categoryFilter) return false;
      if (subcategoryFilter) {
        if (area === "cards") { if (!matchesCardSubcategory(a, subcategoryFilter)) return false; }
        else if ((a.subcategory ?? "") !== subcategoryFilter) return false;
      }
      const rowAttrs = (a.attributes ?? {}) as Record<string, unknown>;
      for (const [k, v] of Object.entries(attrFilters)) {
        if (String(rowAttrs[k] ?? "").toLowerCase() !== v.toLowerCase()) return false;
      }
      const rowPrice = a.current_bid_pence ?? a.starting_price_pence;
      if (priceMin != null && Number.isFinite(priceMin) && rowPrice < priceMin) return false;
      if (priceMax != null && Number.isFinite(priceMax) && rowPrice > priceMax) return false;
      const rowProduct = ((a as { product_type?: string | null }).product_type ?? "trading_card").toLowerCase();
      if (productTypeFilter && rowProduct !== productTypeFilter) return false;
      const rowAccessory = ((a as { accessory_category?: string | null }).accessory_category ?? "").toLowerCase();
      if (accessoryCategoryFilter && rowAccessory !== accessoryCategoryFilter) return false;
      const rowType = (a as { card_type?: string | null }).card_type ?? null;
      const rowGrader = (a.grading_company ?? "").toUpperCase();
      const rowGrade = (a.grade ?? "").trim();
      const rowBrand = ((a as { brand?: string | null }).brand ?? "").trim();
      const rowCondition = ((a as { condition?: string | null }).condition ?? "").trim();
      const rowGame = ((a as { compatible_game?: string | null }).compatible_game ?? "").trim();
      const rowLine = ((a as { product_line_set?: string | null }).product_line_set ?? "").trim();
      const rowLanguage = ((a as { card_language?: string | null }).card_language ?? "").trim();
      const rowFinish = ((a as { finish?: string | null }).finish ?? "").trim();
      const rowEdition = ((a as { edition?: string | null }).edition ?? "").trim();
      const rowCardSet = ((a as { card_set?: string | null }).card_set ?? "").trim();
      if (cardTypeFilter && rowType !== cardTypeFilter) {
        // legacy rows without card_type: treat RAW grading_company as "raw", else "graded"
        const inferred = rowGrader && rowGrader !== "RAW" ? "graded" : "raw";
        if (inferred !== cardTypeFilter) return false;
      }
      if (graderFilter && rowGrader !== graderFilter) return false;
      if (gradeFilter && rowGrade !== gradeFilter) return false;
      if (brandFilter && rowBrand.toLowerCase() !== brandFilter.toLowerCase()) return false;
      if (conditionFilter && rowCondition.toLowerCase() !== conditionFilter.toLowerCase()) return false;
      if (compatibleGameFilter && rowGame.toLowerCase() !== compatibleGameFilter.toLowerCase()) return false;
      if (productLineFilter && rowLine.toLowerCase() !== productLineFilter.toLowerCase()) return false;
      if (cardLanguageFilter && rowLanguage.toLowerCase() !== cardLanguageFilter.toLowerCase()) return false;
      if (finishFilter && rowFinish.toLowerCase() !== finishFilter.toLowerCase()) return false;
      if (editionFilter && rowEdition.toLowerCase() !== editionFilter.toLowerCase()) return false;
      if (cardSetFilter && !rowCardSet.toLowerCase().includes(cardSetFilter.toLowerCase())) return false;
      const rowTcgGame = ((a as { trading_card_game?: string | null }).trading_card_game ?? "").toLowerCase();
      const rowRarity = ((a as { rarity?: string | null }).rarity ?? "").toLowerCase();
      const rowAuto = !!(a as { is_autographed?: boolean | null }).is_autographed;
      const rowSerial = !!(a as { is_serial_numbered?: boolean | null }).is_serial_numbered;
      if (tcgGameFilter && rowTcgGame !== tcgGameFilter.toLowerCase()) return false;
      if (rarityFilter && rowRarity !== rarityFilter.toLowerCase()) return false;
      if (autographedFilter === "yes" && !rowAuto) return false;
      if (serialNumberedFilter === "yes" && !rowSerial) return false;
      if ((trustVerified || trustPartner || trustFeatured) && trustSets) {
        const sid = a.seller_id ?? "";
        if (!sid) return false;
        if (trustVerified && !trustSets.verified.has(sid)) return false;
        if (trustPartner && !trustSets.officialPartners.has(sid)) return false;
        if (trustFeatured && !trustSets.featuredShops.has(sid)) return false;
      }
      return true;
    });
  }, [qFiltered, categoryFilter, subcategoryFilter, attrFilters, priceMin, priceMax, productTypeFilter, accessoryCategoryFilter, cardTypeFilter, graderFilter, gradeFilter, brandFilter, conditionFilter, compatibleGameFilter, productLineFilter, cardLanguageFilter, finishFilter, editionFilter, cardSetFilter, tcgGameFilter, rarityFilter, autographedFilter, serialNumberedFilter, trustVerified, trustPartner, trustFeatured, trustSets]);
  const shown = useMemo(
    () => (smartFilters ? urlFiltered.filter((a) => matchesSmartFilters(a as never, smartFilters)) : urlFiltered),
    [urlFiltered, smartFilters],
  );

  // Brands / conditions / product-lines available in the current result set
  // (before that specific filter is applied), so options stay relevant.
  const availableBrands = useMemo(() => {
    const set = new Map<string, string>();
    for (const a of qFiltered) {
      const b = ((a as { brand?: string | null }).brand ?? "").trim();
      if (!b) continue;
      const key = b.toLowerCase();
      if (!set.has(key)) set.set(key, b);
    }
    return [...set.values()].sort((a, b) => a.localeCompare(b));
  }, [qFiltered]);
  // Category-specific attribute filters (Collectibles / Fashion), options from live results.
  const attrFilterOptions = useMemo(() => {
    if (!categoryFilter || categoryFilter === "cards") return [];
    const subs = subcategoryFilter ? subcategoriesFor(categoryFilter).filter((s) => s.value === subcategoryFilter) : subcategoriesFor(categoryFilter);
    const fields = new Map<string, AttributeField>();
    for (const s of subs) for (const f of s.fields) if (f.filter && !fields.has(f.key)) fields.set(f.key, f);
    const rows = qFiltered.filter((a) => rowCategory(a) === categoryFilter && (!subcategoryFilter || a.subcategory === subcategoryFilter));
    return [...fields.values()].map((field) => {
      const vals = new Set<string>();
      for (const a of rows) {
        const v = ((a.attributes ?? {}) as Record<string, unknown>)[field.key];
        if (typeof v === "string" && v.trim()) vals.add(v.trim());
      }
      return { field, values: [...vals].sort((x, y) => x.localeCompare(y, undefined, { numeric: true })) };
    }).filter((o) => o.values.length > 0 || attrFilters[o.field.key]);
  }, [qFiltered, categoryFilter, subcategoryFilter, attrFilters]);
  const availableConditions = useMemo(() => {
    const set = new Set<string>();
    for (const a of qFiltered) {
      const c = ((a as { condition?: string | null }).condition ?? "").trim();
      if (c) set.add(c);
    }
    return [...set].sort();
  }, [qFiltered]);
  const availableProductLines = useMemo(() => {
    const set = new Map<string, string>();
    for (const a of qFiltered) {
      const b = ((a as { product_line_set?: string | null }).product_line_set ?? "").trim();
      if (!b) continue;
      const key = b.toLowerCase();
      if (!set.has(key)) set.set(key, b);
    }
    return [...set.values()].sort((a, b) => a.localeCompare(b));
  }, [qFiltered]);

  async function runSmartSearch() {
    const q = smartQuery.trim();
    if (!q) {
      setSmartFilters(null);
      return;
    }
    setSmartRunning(true);
    try {
      const res = await parseFn({ data: { query: q } });
      setSmartFilters(res.filters);
      toast.success(res.source === "ai" ? t("browse.smartSearchAiApplied", "Smart search applied") : t("browse.smartSearchKeywordApplied", "Keyword search applied"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("browse.smartSearchFailed", "Smart search failed"));
    } finally {
      setSmartRunning(false);
    }
  }

  const areaH1: Record<string, string> = { cards: "Trading Card Auctions", collectibles: "Collectibles Auctions", fashion: "Fashion Auctions" };
  const h1 = category ? t("browse.categoryH1", "{category} Card Auctions", { category: category.label }) : areaH1[categoryFilter] ?? t("browse.defaultH1", "Live Auctions");
  const introCopy = category
    ? t("browse.categoryIntro", "Live UK auctions for {category} trading cards on BidNova Auctions. Priced in GBP, protected by Stripe-secured payments and covered by our buyer protection guarantee. Filter by Buy It Now, grading company (PSA, BGS, CGC, ACE), price and time remaining.", { category: category.label })
    : categoryFilter === "collectibles"
    ? "Funko Pop!, anime and action figures, statues, designer toys and collectible merchandise — live UK auctions priced in GBP with Stripe-secured payments."
    : categoryFilter === "fashion"
    ? "T-shirts, hoodies, jackets, sneakers, hats, bags and accessories — live UK auctions priced in GBP with Stripe-secured payments."
    : t("browse.defaultIntro", "Browse every live trading card auction on BidNova Auctions — the UK's trusted marketplace for graded Pokémon, One Piece, Yu-Gi-Oh!, Dragon Ball, Magic: The Gathering and sports cards. Every listing is priced in GBP, protected by Stripe-secured payments and covered by our buyer protection guarantee. Filter by Buy It Now, grading company (PSA, BGS, CGC, ACE), price and time remaining, or use AI Smart Search to describe exactly what you're looking for.");

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">{t("browse.eyebrow", "Marketplace")}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">{h1}</h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">{introCopy}</p>
        <nav aria-label="Browse categories" className="mt-5 flex flex-wrap gap-2 text-sm">
          {categoryFilter && categoryFilter !== "cards" && subcategoriesFor(categoryFilter).map((sc) => (
            <Link key={sc.value} to="/auctions" search={{ category: categoryFilter, subcategory: sc.value } as never}
              className={`rounded-full border px-3 py-1 transition-colors ${subcategoryFilter === sc.value ? "border-primary bg-primary/20 text-primary" : "border-primary/30 bg-primary/5 hover:bg-primary/10"}`}>
              {sc.label}
            </Link>
          ))}
          {showCardFilters && BROWSE_CATEGORIES.slice(0, 8).map((c) => {
            const active = category?.slug === c.slug;
            return (
              <Link
                key={c.slug}
                to="/auctions"
                search={{ q: c.q }}
                className={`rounded-full border px-3 py-1 transition-colors ${
                  active
                    ? "border-gold bg-gold/20 text-gold"
                    : "border-gold/30 bg-gold/5 hover:bg-gold/10"
                }`}
              >
                {c.label}
              </Link>
            );
          })}
          <Link to="/auctions" search={{ q: "" }} className="rounded-full border border-border px-3 py-1 hover:bg-muted">{t("browse.allCategories", "All categories")}</Link>
          <Link to="/trends" className="rounded-full border border-border px-3 py-1 hover:bg-muted">{t("browse.marketTrends", "Market trends")}</Link>
          <Link to="/sell" className="rounded-full border border-border px-3 py-1 hover:bg-muted">{t("browse.sellAnItem", "Sell an item")}</Link>
        </nav>
      </div>




      <form
        onSubmit={(e) => { e.preventDefault(); runSmartSearch(); }}
        className="mb-4 flex flex-wrap gap-2 items-center rounded-lg border border-gold/20 bg-gold/5 p-3"
      >
        <Sparkles className="h-4 w-4 text-gold" />
        <Input
          value={smartQuery}
          onChange={(e) => setSmartQuery(e.target.value)}
          placeholder={t("browse.smartSearchPlaceholder", 'Try: "PSA 10 Charizard under £500" or "Japanese One Piece manga rare"')}
          className="flex-1 min-w-[240px] bg-background/60"
        />
        <Button type="submit" size="sm" className="bg-gold-gradient hover:opacity-90" disabled={smartRunning}>
          {smartRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : t("browse.smartSearchButton", "AI Smart Search")}
        </Button>
        {smartFilters && (
          <Button type="button" size="sm" variant="outline" onClick={() => { setSmartQuery(""); setSmartFilters(null); }}>
            {t("browse.smartSearchClear", "Clear")}
          </Button>
        )}
      </form>

      <div className="mb-6 flex flex-wrap gap-3">
        <div className="min-w-[200px]">
          <Select value={purchase} onValueChange={(v) => setPurchase(v as PurchaseFilter)}>
            <SelectTrigger aria-label="Filter by purchase method"><SelectValue placeholder={t("browse.purchaseMethodPlaceholder", "Purchase method")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("browse.purchaseAll", "All listings")}</SelectItem>
              <SelectItem value="auction_only">{t("browse.purchaseAuctionOnly", "Auction only")}</SelectItem>
              <SelectItem value="buy_now">{t("browse.purchaseBuyNow", "Buy It Now available")}</SelectItem>
              <SelectItem value="auction_plus_bin">{t("browse.purchaseAuctionPlusBin", "Auction + Buy It Now")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
            <SelectTrigger aria-label="Sort results"><SelectValue placeholder={t("browse.sortPlaceholder", "Sort")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ending_soon">{t("browse.sortEndingSoon", "Ending soon")}</SelectItem>
              <SelectItem value="newly_listed">{t("browse.sortNewlyListed", "Newly listed")}</SelectItem>
              <SelectItem value="current_bid_high">{t("browse.sortCurrentBidHigh", "Current bid: high to low")}</SelectItem>
              <SelectItem value="bin_price_low">{t("browse.sortBinLow", "Buy It Now: low to high")}</SelectItem>
              <SelectItem value="bin_price_high">{t("browse.sortBinHigh", "Buy It Now: high to low")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[170px]">
          <Select value={categoryFilter || "any"} onValueChange={(v) => updateSearch({ category: v === "any" ? undefined : v, subcategory: undefined, attrs: undefined })}>
            <SelectTrigger aria-label="Filter by marketplace category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All categories</SelectItem>
              {MAIN_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {categoryFilter && (
          <div className="min-w-[190px]">
            <Select value={subcategoryFilter || "any"} onValueChange={(v) => updateSearch({ subcategory: v === "any" ? undefined : v, attrs: undefined })}>
              <SelectTrigger aria-label="Filter by subcategory"><SelectValue placeholder="Subcategory" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">All subcategories</SelectItem>
                {(categoryFilter === "cards" ? CARD_SUBCATEGORIES : subcategoriesFor(categoryFilter)).map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {attrFilterOptions.map(({ field, values }) => (
          <div key={field.key} className="min-w-[160px]">
            <Select value={attrFilters[field.key] || "any"} onValueChange={(v) => setAttrFilter(field.key, v === "any" ? undefined : v)}>
              <SelectTrigger aria-label={`Filter by ${field.label}`}><SelectValue placeholder={field.label} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any {field.label.toLowerCase()}</SelectItem>
                {values.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <Input type="number" inputMode="decimal" min={0} aria-label="Minimum price (£)" placeholder="Min £" className="w-24" defaultValue={search.price_min ?? ""} onBlur={(e) => updateSearch({ price_min: e.target.value || undefined })} />
          <span className="text-muted-foreground text-xs">–</span>
          <Input type="number" inputMode="decimal" min={0} aria-label="Maximum price (£)" placeholder="Max £" className="w-24" defaultValue={search.price_max ?? ""} onBlur={(e) => updateSearch({ price_max: e.target.value || undefined })} />
        </div>
        {showCardFilters && (<>
        <div className="min-w-[180px]">
          <Select
            value={productTypeFilter || "any"}
            onValueChange={(v) => updateSearch({ product_type: v === "any" ? undefined : v })}
          >
            <SelectTrigger aria-label="Filter by product type"><SelectValue placeholder={t("browse.productTypePlaceholder", "Product type")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyProductType", "Any product type")}</SelectItem>
              {PRODUCT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{PRODUCT_TYPE_LABEL[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {productTypeFilter === "accessory" && (
          <div className="min-w-[200px]">
            <Select
              value={accessoryCategoryFilter || "any"}
              onValueChange={(v) => updateSearch({ accessory_category: v === "any" ? undefined : v })}
            >
              <SelectTrigger aria-label="Filter by accessory category"><SelectValue placeholder={t("browse.accessoryCategoryPlaceholder", "Accessory category")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("browse.anyAccessory", "Any accessory")}</SelectItem>
                {ACCESSORY_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{ACCESSORY_CATEGORY_LABEL[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="min-w-[180px]">
          <Select
            value={cardTypeFilter || "any"}
            onValueChange={(v) => updateSearch({ card_type: v === "any" ? undefined : v })}
          >
            <SelectTrigger aria-label="Filter by card type"><SelectValue placeholder={t("browse.cardTypePlaceholder", "Card type")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyCardType", "Any card type")}</SelectItem>
              {CARD_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{CARD_TYPE_LABEL[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Select
            value={graderFilter || "any"}
            onValueChange={(v) => updateSearch({ grader: v === "any" ? undefined : v })}
          >
            <SelectTrigger aria-label="Filter by grading company"><SelectValue placeholder={t("browse.graderPlaceholder", "Grading company")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyGrader", "Any grading company")}</SelectItem>
              {SELLER_GRADING_COMPANIES.map((g) => (
                <SelectItem key={g} value={g}>{g === "OTHER" ? t("browse.othersOption", "Others") : GRADER_SHORT[g]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Select
            value={gradeFilter || "any"}
            onValueChange={(v) => updateSearch({ grade: v === "any" ? undefined : v })}
          >
            <SelectTrigger aria-label="Filter by grade"><SelectValue placeholder={t("browse.gradePlaceholder", "Grade")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyGrade", "Any grade")}</SelectItem>
              {sortedGradeFilters("desc").map((g) => (
                <SelectItem key={g} value={g}>{formatGradeLabel(g) || g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        </>)}
        {availableBrands.length > 0 && (
          <div className="min-w-[200px]">
            <Select
              value={brandFilter || "any"}
              onValueChange={(v) => updateSearch({ brand: v === "any" ? undefined : v })}
            >
              <SelectTrigger aria-label="Filter by brand"><SelectValue placeholder={t("browse.brandPlaceholder", "Brand")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("browse.anyBrand", "Any brand")}</SelectItem>
                {availableBrands.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {availableConditions.length > 0 && (
          <div className="min-w-[180px]">
            <Select
              value={conditionFilter || "any"}
              onValueChange={(v) => updateSearch({ condition: v === "any" ? undefined : v })}
            >
              <SelectTrigger aria-label="Filter by condition"><SelectValue placeholder={t("browse.conditionPlaceholder", "Condition")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("browse.anyCondition", "Any condition")}</SelectItem>
                {availableConditions.map((c) => (
                  <SelectItem key={c} value={c}>{labelForCondition(c) || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {showCardFilters && (<>
        <div className="min-w-[200px]">
          <Select
            value={compatibleGameFilter || "any"}
            onValueChange={(v) => updateSearch({ compatible_game: v === "any" ? undefined : v })}
          >
            <SelectTrigger aria-label="Filter by compatible game"><SelectValue placeholder={t("browse.gamePlaceholder", "Compatible game")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyGame", "Any game")}</SelectItem>
              {COMPATIBLE_GAMES.filter((g) => g !== "Other").map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {availableProductLines.length > 0 && (
          <div className="min-w-[200px]">
            <Select
              value={productLineFilter || "any"}
              onValueChange={(v) => updateSearch({ product_line_set: v === "any" ? undefined : v })}
            >
              <SelectTrigger aria-label="Filter by product line or set"><SelectValue placeholder={t("browse.productLinePlaceholder", "Product line / set")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("browse.anySetLine", "Any set / line")}</SelectItem>
                {availableProductLines.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="min-w-[180px]">
          <Select value={tcgGameFilter || "any"} onValueChange={(v) => updateSearch({ tcg_game: v === "any" ? undefined : v })}>
            <SelectTrigger aria-label="Filter by trading card game"><SelectValue placeholder={t("browse.tcgGamePlaceholder", "Game")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyGame", "Any game")}</SelectItem>
              {TCG_GAMES.map((g) => (<SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Select value={rarityFilter || "any"} onValueChange={(v) => updateSearch({ rarity: v === "any" ? undefined : v })}>
            <SelectTrigger aria-label="Filter by rarity"><SelectValue placeholder={t("browse.rarityPlaceholder", "Rarity")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyRarity", "Any rarity")}</SelectItem>
              {RARITY_OPTIONS.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm rounded-md border border-border/60 bg-surface-2/40 px-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autographedFilter === "yes"}
            onChange={(e) => updateSearch({ autographed: e.target.checked ? "yes" : undefined })}
            className="h-4 w-4 accent-gold"
            aria-label="Autographed only"
          />
          {t("browse.autographedLabel", "Autographed")}
        </label>
        <label className="inline-flex items-center gap-2 text-sm rounded-md border border-border/60 bg-surface-2/40 px-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={serialNumberedFilter === "yes"}
            onChange={(e) => updateSearch({ serial_numbered: e.target.checked ? "yes" : undefined })}
            className="h-4 w-4 accent-gold"
            aria-label="Serial numbered only"
          />
          {t("browse.serialLabel", "Serial #")}
        </label>
        </>)}
        <div className="inline-flex flex-wrap items-center gap-1.5 rounded-md border border-border/60 bg-surface-2/40 px-2 py-1.5 text-xs">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground pr-1">{t("browse.trustLabel", "Trust")}</span>
          <TrustChip label={t("browse.trustVerifiedChip", "Verified")} active={trustVerified} onClick={() => toggleTrust("verified")} tone="emerald" />
          <TrustChip label={t("browse.trustPartnerChip", "Official Partner")} active={trustPartner} onClick={() => toggleTrust("partner")} tone="purple" />
          <TrustChip label={t("browse.trustFeaturedChip", "Featured Shop")} active={trustFeatured} onClick={() => toggleTrust("featured")} tone="gold" />
        </div>
        {showCardFilters && (<>
        <div className="min-w-[180px]">
          <Select value={cardLanguageFilter || "any"} onValueChange={(v) => updateSearch({ card_language: v === "any" ? undefined : v })}>
            <SelectTrigger aria-label="Filter by card language"><SelectValue placeholder={t("browse.languagePlaceholder", "Language")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyLanguage", "Any language")}</SelectItem>
              {CARD_LANGUAGES.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Select value={finishFilter || "any"} onValueChange={(v) => updateSearch({ finish: v === "any" ? undefined : v })}>
            <SelectTrigger aria-label="Filter by finish"><SelectValue placeholder={t("browse.finishPlaceholder", "Finish")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyFinish", "Any finish")}</SelectItem>
              {FINISH_OPTIONS.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Select value={editionFilter || "any"} onValueChange={(v) => updateSearch({ edition: v === "any" ? undefined : v })}>
            <SelectTrigger aria-label="Filter by edition"><SelectValue placeholder={t("browse.editionPlaceholder", "Edition")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t("browse.anyEdition", "Any edition")}</SelectItem>
              {EDITION_OPTIONS.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Input
            aria-label="Filter by card set"
            placeholder={t("browse.cardSetPlaceholder", "Card set…")}
            value={cardSetFilter}
            onChange={(e) => updateSearch({ card_set: e.target.value || undefined })}
          />
        </div>
        </>)}
      </div>

      {(productTypeFilter || accessoryCategoryFilter || cardTypeFilter || graderFilter || gradeFilter || brandFilter || conditionFilter || compatibleGameFilter || productLineFilter || cardLanguageFilter || finishFilter || editionFilter || cardSetFilter || tcgGameFilter || rarityFilter || autographedFilter || serialNumberedFilter || trustVerified || trustPartner || trustFeatured) && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t("browse.activeFiltersLabel", "Active filters:")}</span>
          {tcgGameFilter && (
            <button type="button" onClick={() => updateSearch({ tcg_game: undefined })} className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20" aria-label="Remove game filter">
              {t("browse.removeGameFilter", "Game")}: {TCG_GAME_LABEL[tcgGameFilter] ?? tcgGameFilter} ×
            </button>
          )}
          {rarityFilter && (
            <button type="button" onClick={() => updateSearch({ rarity: undefined })} className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20" aria-label="Remove rarity filter">
              {t("browse.removeRarityFilter", "Rarity")}: {RARITY_LABEL[rarityFilter] ?? rarityFilter} ×
            </button>
          )}
          {autographedFilter === "yes" && (
            <button type="button" onClick={() => updateSearch({ autographed: undefined })} className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20" aria-label="Remove autographed filter">
              {t("browse.removeAutographedFilter", "Autographed")} ×
            </button>
          )}
          {serialNumberedFilter === "yes" && (
            <button type="button" onClick={() => updateSearch({ serial_numbered: undefined })} className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20" aria-label="Remove serial numbered filter">
              {t("browse.removeSerialFilter", "Serial #")} ×
            </button>
          )}
          {trustVerified && (
            <button type="button" onClick={() => toggleTrust("verified")} className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-emerald-300 hover:bg-emerald-500/20" aria-label="Remove verified sellers filter">
              {t("browse.removeVerifiedFilter", "Verified sellers")} ×
            </button>
          )}
          {trustPartner && (
            <button type="button" onClick={() => toggleTrust("partner")} className="rounded-full border border-purple-400/40 bg-purple-500/10 px-3 py-1 text-purple-200 hover:bg-purple-500/20" aria-label="Remove official partners filter">
              {t("browse.removePartnerFilter", "Official partners")} ×
            </button>
          )}
          {trustFeatured && (
            <button type="button" onClick={() => toggleTrust("featured")} className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-gold hover:bg-gold/20" aria-label="Remove featured shops filter">
              {t("browse.removeFeaturedFilter", "Featured shops")} ×
            </button>
          )}


          {productTypeFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ product_type: undefined, accessory_category: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove product type filter ${productTypeFilter}`}
            >
              {t("browse.removeProductFilter", "Product")}: {PRODUCT_TYPE_LABEL[productTypeFilter as keyof typeof PRODUCT_TYPE_LABEL] ?? productTypeFilter} ×
            </button>
          )}
          {accessoryCategoryFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ accessory_category: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove accessory category filter ${accessoryCategoryFilter}`}
            >
              {t("browse.removeAccessoryFilter", "Accessory")}: {ACCESSORY_CATEGORY_LABEL[accessoryCategoryFilter as keyof typeof ACCESSORY_CATEGORY_LABEL] ?? accessoryCategoryFilter} ×
            </button>
          )}
          {cardTypeFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ card_type: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove card type filter ${cardTypeFilter}`}
            >
              Type: {CARD_TYPE_LABEL[cardTypeFilter as keyof typeof CARD_TYPE_LABEL] ?? cardTypeFilter} ×
            </button>
          )}
          {graderFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ grader: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove grader filter ${graderFilter}`}
            >
              Grader: {GRADER_SHORT[graderFilter as keyof typeof GRADER_SHORT] ?? graderFilter} ×
            </button>
          )}
          {gradeFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ grade: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove grade filter ${gradeFilter}`}
            >
              Grade: {gradeFilter} ×
            </button>
          )}
          {brandFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ brand: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove brand filter ${brandFilter}`}
            >
              Brand: {brandFilter} ×
            </button>
          )}
          {conditionFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ condition: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove condition filter ${conditionFilter}`}
            >
              Condition: {labelForCondition(conditionFilter) || conditionFilter} ×
            </button>
          )}
          {compatibleGameFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ compatible_game: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove compatible game filter ${compatibleGameFilter}`}
            >
              Game: {compatibleGameFilter} ×
            </button>
          )}
          {productLineFilter && (
            <button
              type="button"
              onClick={() => updateSearch({ product_line_set: undefined })}
              className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 hover:bg-gold/20"
              aria-label={`Remove product line filter ${productLineFilter}`}
            >
              Set / Line: {productLineFilter} ×
            </button>
          )}
          <button
            type="button"
            onClick={clearAllFilters}
            className="rounded-full border border-border px-3 py-1 hover:bg-muted"
          >
            {t("browse.clearAll", "Clear all")}
          </button>
        </div>
      )}


      {isLoading ? (
        <CardGridSkeleton count={8} />
      ) : isError ? (
        <ErrorState
          description={t("browse.loadError", "We couldn't load the live auctions.")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      ) : (
        <AuctionGrid auctions={shown} emptyText={t("browse.noneMatch", "No live auctions match your filter right now — try widening it, or check back shortly as new listings go live throughout the day.")} />
      )}

      <section aria-labelledby="how-bidding-works" className="mt-16 grid gap-8 md:grid-cols-2">
        <div>
          <h2 id="how-bidding-works" className="font-display text-2xl">{t("browse.howBiddingWorksTitle", "How bidding works")}</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("browse.howBiddingWorksBody", "Every auction on BidNova runs in GBP with a clear starting price, current bid and countdown to close. Place a bid at or above the next valid increment and, if you're outbid, we'll notify you instantly so you can respond. Some listings also offer a Buy It Now price for immediate purchase. Auctions with activity in the final minutes are automatically extended so no bidder can be sniped at the last second.")}
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc pl-5">
            <li>{t("browse.biddingPoint1", "Bid transparently — see every bid, bidder count and time remaining.")}</li>
            <li>{t("browse.biddingPoint2", "Automatic anti-sniping extension protects fair competition.")}</li>
            <li>{t("browse.biddingPoint3", "Winning bidders check out securely with card, Apple Pay or Google Pay via Stripe.")}</li>
            <li>{t("browse.biddingPoint4", "Sellers ship tracked; you confirm delivery before funds release.")}</li>
          </ul>
        </div>
        <div>
          <h2 className="font-display text-2xl">{t("browse.buyerProtectionTitle", "Buyer protection & seller trust")}</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("browse.buyerProtectionBody", "BidNova Auctions is a UK-registered, fully GDPR-compliant marketplace. Payments are processed by Stripe and held until you confirm the card arrived as described. Sellers are verified, carry public ratings and are governed by our Seller Agreement — with a formal dispute process if anything isn't right.")}
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc pl-5">
            <li>{t("browse.protectionPoint1", "Secure Stripe payments — seller payouts only release after delivery confirmation.")}</li>
            <li>{t("browse.protectionPoint2", "Verified sellers with public ratings and shipment tracking.")}</li>
            <li>{t("browse.protectionPoint3", "Formal dispute resolution for damaged, missing or misdescribed cards.")}</li>
            <li>{t("browse.protectionPoint4", "UK-registered platform, prices in GBP, governed by the laws of England & Wales.")}</li>
          </ul>
        </div>
      </section>
    </div>
  );

}

function TrustChip({
  label,
  active,
  onClick,
  tone,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone: "emerald" | "purple" | "gold";
}) {
  const tones = {
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    purple: "border-purple-400/40 bg-purple-500/10 text-purple-200",
    gold: "border-gold/40 bg-gold/10 text-gold",
  } as const;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${
        active ? tones[tone] : "border-border/60 bg-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}
